// Copyright (C) 2024 Streaming Server Wrapper for Browser Streaming

const { BROWSER_CONFIG } = require('./cloudflare-config');
const BrowserStreamingService = require('./browser-streaming');

class StreamingServerWrapper {
    constructor() {
        this.browserStreamingService = BrowserStreamingService; // It's already a singleton instance
        this.originalStreamingServer = null;
        this.isBrowserMode = BROWSER_CONFIG.enableBrowserStreaming;
    }

    // Wrap the streaming server to use browser streaming
    wrapStreamingServer(originalServer) {
        this.originalStreamingServer = originalServer;
        
        if (!this.isBrowserMode) {
            // For cloud deployment (browser streaming disabled), still wrap to provide cloud-compatible URLs
            console.log('Browser streaming disabled, creating cloud deployment wrapper');
            return new Proxy(originalServer, {
                get: (target, prop) => {
                    if (prop === 'selected' && target.selected) {
                        // Even without browser streaming, ensure URLs are cloud-compatible
                        return this.wrapSelectedStreamForCloud(target.selected);
                    }
                    return target[prop];
                }
            });
        }

        // Create a proxy that intercepts streaming requests
        return new Proxy(originalServer, {
            get: (target, prop) => {
                if (prop === 'selected' && target.selected) {
                    return this.wrapSelectedStream(target.selected);
                }
                if (prop === 'baseUrl' && BROWSER_CONFIG.enableBrowserStreaming) {
                    // Return a virtual URL that indicates browser mode
                    return 'browser://localhost:8080';
                }
                return target[prop];
            }
        });
    }

    // Wrap the selected stream to use browser streaming
    wrapSelectedStream(originalSelected) {
        if (!originalSelected || !originalSelected.transportUrl) {
            return originalSelected;
        }

        // Create a new transport URL that our browser streaming service can handle
        const browserTransportUrl = this.createBrowserTransportUrl(originalSelected.transportUrl);

        return {
            ...originalSelected,
            transportUrl: browserTransportUrl,
            statistics: originalSelected.statistics || null
        };
    }

    // Wrap selected stream for cloud deployment (browser streaming disabled)
    wrapSelectedStreamForCloud(originalSelected) {
        if (!originalSelected || !originalSelected.transportUrl) {
            return originalSelected;
        }

        // For cloud deployment, ensure URLs are properly formatted and accessible
        const cloudTransportUrl = this.ensureCloudCompatibleUrl(originalSelected.transportUrl);

        return {
            ...originalSelected,
            transportUrl: cloudTransportUrl,
            statistics: originalSelected.statistics || null
        };
    }

    // Ensure URL is cloud-compatible
    ensureCloudCompatibleUrl(url) {
        if (!url) return url;
        
        // Convert magnet/torrent URLs to HTTP URLs if needed
        if (url.startsWith('magnet:') || url.startsWith('torrent:')) {
            console.log('Converting magnet/torrent URL to cloud-compatible format:', url);
            return this.createCloudStreamUrl(url);
        }
        
        // Ensure HTTPS for cloud deployment
        if (url.startsWith('http://') && this.isCloudEnvironment()) {
            return url.replace('http://', 'https://');
        }
        
        return url;
    }

    // Create cloud-compatible stream URL
    createCloudStreamUrl(originalUrl) {
        // For cloud deployment, create a URL that can be handled by the browser directly
        // This could be a Cloudflare Worker URL or similar cloud service
        const encodedUrl = encodeURIComponent(originalUrl);
        return `https://cloud-stream.stremio.workers.dev/stream?url=${encodedUrl}`;
    }

    // Create a browser-compatible transport URL
    createBrowserTransportUrl(originalUrl) {
        // If it's already a Cloudflare optimizer URL, use it directly
        const cloudflareOptimizers = [
            'torrentio.strem.fun',
            'comet.elfhosted.com',
            'mediafusion.elfhosted.com',
            'annatar.elfhosted.com',
            'jackettio.elfhosted.com',
            'orionoid.com',
            'stremthru.elfhosted.com'
        ];

        try {
            const urlObj = new URL(originalUrl);
            if (cloudflareOptimizers.some(domain => urlObj.hostname.includes(domain))) {
                return originalUrl;
            }
        } catch (error) {
            // If URL parsing fails, continue with transformation
        }

        // Convert magnet/torrent links to Cloudflare format
        if (originalUrl.includes('magnet:') || originalUrl.includes('.torrent')) {
            return `https://comet.elfhosted.com/stream?url=${encodeURIComponent(originalUrl)}`;
        }

        // For other URLs, ensure they're HTTPS and CORS-enabled
        if (originalUrl.startsWith('http://')) {
            return originalUrl.replace('http://', 'https://');
        }

        return originalUrl;
    }

    // Initialize browser streaming service
    async initializeBrowserStreaming() {
        if (!this.isBrowserMode) {
            return false;
        }

        try {
            // Check browser compatibility instead of calling non-existent initialize method
            const compatibility = this.browserStreamingService.checkBrowserCompatibility();
            console.log('Browser streaming compatibility check:', compatibility);
            
            // Return true if basic features are available
            const isSupported = compatibility.fetch && compatibility.cors;
            console.log('Browser streaming service initialized successfully. Supported:', isSupported);
            return isSupported;
        } catch (error) {
            console.error('Failed to initialize browser streaming service:', error);
            return false;
        }
    }

    // Check if browser streaming is available
    isBrowserStreamingAvailable() {
        if (!this.isBrowserMode) {
            return false;
        }
        
        try {
            const compatibility = this.browserStreamingService.checkBrowserCompatibility();
            return compatibility.fetch && compatibility.cors;
        } catch (error) {
            console.error('Error checking browser streaming availability:', error);
            return false;
        }
    }

    // Get browser streaming service instance
    getBrowserStreamingService() {
        return this.browserStreamingService;
    }
}

// Create singleton instance
const streamingServerWrapper = new StreamingServerWrapper();

module.exports = streamingServerWrapper;